import React, {FC} from 'react';

export interface IAlert<T = unknown> {
    subject: string;
    content: T;
    level?: AlertLevel;
    date?: Date | null;
}

export type AlertLevel = 'warning' | 'message';

export interface AlertHandler<T = unknown> {
    subject: string;
    Manager: FC<{content: unknown; children: (content: T) => React.JSX.Element}>;
    Display: FC<{content: T}>;
    Catcher?: FC<{error: unknown}>;
    Awaiter?: FC;
}

export namespace AlertHandler {
    type StaticAlertHandler = AlertHandler<null | undefined | void>;
    export const createStatic = (
        subject: string,
        handler:
            | StaticAlertHandler['Display']
            | {
                  Display: StaticAlertHandler['Display'];
                  Catcher?: StaticAlertHandler['Catcher'];
                  Awaiter?: StaticAlertHandler['Awaiter'];
              }
    ): StaticAlertHandler => {
        if (typeof handler === 'function') {
            handler = {Display: handler};
        }
        handler.Display.displayName = `${subject}.Display`;
        return {
            Manager: ({children}) => children(null),
            ...handler,
            subject,
        };
    };

    export const createStaticBatch = <T extends Record<string, StaticAlertHandler['Display']>>(batch: T) => {
        return Object.fromEntries(
            Object.entries(batch).map(([key, Display]) => {
                return [key, createStatic(key, Display)];
            })
        ) as {
            [K in keyof T]: StaticAlertHandler;
        };
    };

    export const define = <T>(
        subject: string,
        handler: {
            Display: AlertHandler<T>['Display'];
            Manager: AlertHandler<T>['Manager'];
            Catcher?: AlertHandler<T>['Catcher'];
            Awaiter?: AlertHandler<T>['Awaiter'];
        }
    ): AlertHandler<T> => {
        return {
            ...handler,
            subject,
        };
    };
}
